import React, { useEffect, useState, useRef } from 'react';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchPublicCampaignByID,
    fetchUserCampaignContests,
    fetchAllCampaigns,
    updateCampaign,
    createUserCampaignContest,
} from '../../api/campaigns';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/LoadingSpinner';
import AdventureMap from '../../components/AdventureMap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

const CampaignDetailPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const { campaignID } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [userCampaignContests, setUserCampaignContests] = useState([]);
    const [userContestsLoaded, setUserContestsLoaded] = useState(!currentUser);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        desc: '',
        minLevel: '',
        prerequisiteCampaignIDs: [],
    });
    const [allCampaigns, setAllCampaigns] = useState([]);
    const hasFetchedCampaigns = useRef(false);
    const hasFetchedData = useRef(false);

    const [isLevelLocked, setIsLevelLocked] = useState(false);
    const [requiredLevel, setRequiredLevel] = useState(null);

    useEffect(() => {
        const fetchCampaignData = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchPublicCampaignByID(campaignID);
                setCampaign(data);
                setFormData({
                    name: data.name,
                    desc: data.desc,
                    minLevel: data.minLevel ?? '',
                    prerequisiteCampaignIDs: (data.prerequisiteCampaignIDs || []).map(String),
                });
                //toast.success('Campaign fetched successfully!');
            } catch (error) {
                setError('Error fetching campaign');
                toast.error('Error fetching campaign: ' + error.message);
            } finally {
                setLoading(false);
            }
        };

        if (!hasFetchedData.current) {
            fetchCampaignData();
            hasFetchedData.current = true; // Set the ref to true after fetching
        }

        if (currentUser) {
            const fetchUserContests = async () => {
                try {
                    const contests = await fetchUserCampaignContests(campaignID);
                    setUserCampaignContests(contests.CampaignContests);
                    setIsLevelLocked(contests.isLevelLocked ?? false);
                    setRequiredLevel(contests.minLevel ?? null);
                } catch (error) {
                    console.error('Error fetching user campaign contests:', error.message);
                } finally {
                    setUserContestsLoaded(true);
                }
            };

            fetchUserContests();
        }
    }, [campaignID, currentUser]);

    useEffect(() => {
        if (editMode && !hasFetchedCampaigns.current) {
            fetchAllCampaigns()
                .then(data => {
                    hasFetchedCampaigns.current = true;
                    setAllCampaigns(data.filter(c => String(c.campaignID) !== String(campaignID)));
                })
                .catch(err => console.error('Failed to fetch campaigns:', err));
        }
    }, [editMode, campaignID]);

    const togglePrereq = id => {
        setFormData(prev => ({
            ...prev,
            prerequisiteCampaignIDs: prev.prerequisiteCampaignIDs.includes(String(id))
                ? prev.prerequisiteCampaignIDs.filter(x => x !== String(id))
                : [...prev.prerequisiteCampaignIDs, String(id)],
        }));
    };

    const handleInputChange = e => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            const updatedCampaign = await updateCampaign(campaignID, {
                ...formData,
                minLevel: formData.minLevel ? Number(formData.minLevel) : null,
                prerequisiteCampaignIDs: formData.prerequisiteCampaignIDs,
            });
            setCampaign(updatedCampaign);
            setEditMode(false);
            toast.success('Campaign updated successfully!');
        } catch (error) {
            setError('Error updating campaign');
            toast.error('Error updating campaign: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <span className="error">{error}</span>;
    }

    if (!campaign) {
        return <span>No campaign found.</span>;
    }

    return (
        <div className="campaign-detail-page campaign-detail-page--map">
            {editMode && (
                <div className="campaign-edit-form">
                    <div>
                        <label>Name:</label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} />
                    </div>
                    <div>
                        <label>Description:</label>
                        <textarea name="desc" value={formData.desc} onChange={handleInputChange} />
                    </div>
                    <div>
                        <label>Minimum Level:</label>
                        <input type="number" name="minLevel" min="1" value={formData.minLevel} onChange={handleInputChange} placeholder="No requirement" />
                    </div>
                    {allCampaigns.length > 0 && (
                        <fieldset className="prereq-picker">
                            <legend>Prerequisite Campaigns:</legend>
                            <div className="prereq-picker__list">
                                {allCampaigns.map(c => (
                                    <label key={c.campaignID} className="prereq-picker__item">
                                        <input
                                            type="checkbox"
                                            checked={formData.prerequisiteCampaignIDs.includes(String(c.campaignID))}
                                            onChange={() => togglePrereq(c.campaignID)}
                                        />
                                        {c.name}
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                    )}
                    <button onClick={handleSave} disabled={loading}>
                        {loading ? 'Saving...' : 'Save'}
                    </button>
                </div>
            )}
            {isLevelLocked && (
                <div className="campaign-level-lock-banner">
                    <FontAwesomeIcon icon={faLock} /> Reach Level {requiredLevel} to play this campaign
                </div>
            )}
            <div className="campaign-contests">
                {!userContestsLoaded ? (
                    <LoadingSpinner />
                ) : campaign.CampaignContests?.length > 0 ? (
                    <AdventureMap
                        campaignName={campaign.name}
                        campaignDesc={campaign.desc}
                        backTo="/campaigns"
                        isAdmin={currentUser && currentUser.isAdmin}
                        onEdit={() => setEditMode(true)}
                        editMode={editMode}
                        onAddContest={editMode && currentUser?.isAdmin
                            ? () => navigate(`/campaign-contests/create?campaignID=${campaignID}`)
                            : undefined}
                        contests={userCampaignContests.length > 0 ? userCampaignContests : campaign.CampaignContests}
                        userCampaignContests={userCampaignContests}
                        currentUser={currentUser}
                        onStartContest={async (campaignContestID) => {
                            if (isLevelLocked) return;
                            try {
                                const newUserCampaignContest = await createUserCampaignContest(
                                    campaignContestID,
                                    currentUser.userID
                                );
                                navigate(`/campaign-contests/${newUserCampaignContest.userCampaignContestID}/play`);
                            } catch (error) {
                                console.error('Error starting campaign contest:', error.message);
                            }
                        }}
                        isLevelLocked={isLevelLocked}
                    />
                ) : (
                    <span>No contests available.</span>
                )}
            </div>
        </div>
    );
};

export default CampaignDetailPage;
